import { PlayerGait, PlayerStance, PLAYER_SPEED, advancePlayerBodyYaw, normalizePlayerYaw, clampPlayerPitch } from "./player-control.js";
export var WaterLocomotion;
(function (WaterLocomotion) {
    WaterLocomotion[WaterLocomotion["Ground"] = 0] = "Ground";
    WaterLocomotion[WaterLocomotion["Shallow"] = 1] = "Shallow";
    WaterLocomotion[WaterLocomotion["Deep"] = 2] = "Deep";
    WaterLocomotion[WaterLocomotion["Swimming"] = 3] = "Swimming";
})(WaterLocomotion || (WaterLocomotion = {}));
export const WATER_THRESHOLDS = Object.freeze({
    shallowEnter: 0.25, shallowExit: 0.20, deepEnter: 0.75, deepExit: 0.65,
    swimEnter: 1.15, swimExit: 0.95, meaningfulImmersion: 0.45,
});
export const SWIM_SPEED = 2.6;
/** 1.78 m rig; pitched SWIM shoulders sit ~1.15 m above root, head ~1.48 m. */
export const SWIM_SURFACE_ANCHOR_HEIGHT = 1.10;
export const WET_DURATION_SECONDS = 55;
export const WATER_VERTICAL_RESPONSE = 8;
export const DEVELOPER_MINIMUM_Y = -512;
export const DEVELOPER_MAXIMUM_Y = 2_048;
export function classifyWaterLocomotion(depth, previous = WaterLocomotion.Ground) {
    if (previous === WaterLocomotion.Swimming && depth >= WATER_THRESHOLDS.swimExit)
        return WaterLocomotion.Swimming;
    if (depth >= WATER_THRESHOLDS.swimEnter)
        return WaterLocomotion.Swimming;
    if (depth >= WATER_THRESHOLDS.deepEnter || (previous >= WaterLocomotion.Deep && depth >= WATER_THRESHOLDS.deepExit))
        return WaterLocomotion.Deep;
    if (depth >= WATER_THRESHOLDS.shallowEnter || (previous >= WaterLocomotion.Shallow && depth >= WATER_THRESHOLDS.shallowExit))
        return WaterLocomotion.Shallow;
    return WaterLocomotion.Ground;
}
/** Continuous ankle-to-deep resistance; deep wading never uses running speed. */
export function waterSpeedMultiplier(depth) {
    if (depth <= WATER_THRESHOLDS.shallowExit)
        return 1;
    if (depth < WATER_THRESHOLDS.deepEnter)
        return 1 - 0.42 * smooth((depth - 0.20) / 0.55);
    return 0.58 - 0.08 * smooth((depth - 0.75) / 0.40);
}
/** The exact authority/prediction step, including inertia, current, collision and hysteresis. */
export function stepPlayerLocomotion(state, input, deltaSeconds, options) {
    const dt = Math.max(0, Math.min(0.25, deltaSeconds));
    const sample = options.sampleWater;
    const before = sample(state.x, state.z);
    const previous = state.locomotion;
    const mode = options.freeFly ? WaterLocomotion.Ground : classifyWaterLocomotion(before.depth, previous);
    const magnitude = Math.hypot(input.x, input.z);
    const intent = magnitude > 0.001;
    const factor = options.speedMultiplier ?? 1;
    const resistance = mode === WaterLocomotion.Ground ? 1 : waterSpeedMultiplier(before.depth);
    const runBlend = mode === WaterLocomotion.Ground ? 1 : 1 - smooth((before.depth - 0.4) / 0.35);
    const crouched = !options.freeFly && mode !== WaterLocomotion.Swimming && input.stance === PlayerStance.Crouched;
    const gait = crouched || mode >= WaterLocomotion.Deep ? PlayerGait.Walk : (input.gait ?? (input.run ? PlayerGait.Run : PlayerGait.Walk));
    if (input.stance !== undefined)
        state.stance = crouched ? PlayerStance.Crouched : PlayerStance.Standing;
    if (input.gait !== undefined)
        state.gait = gait;
    const gaitSpeed = gait === PlayerGait.Sprint ? PLAYER_SPEED.sprint : gait === PlayerGait.Run ? PLAYER_SPEED.run : PLAYER_SPEED.walk;
    const groundSpeed = crouched ? PLAYER_SPEED.crouch : PLAYER_SPEED.walk + (gaitSpeed - PLAYER_SPEED.walk) * runBlend;
    const maximumSpeed = (mode === WaterLocomotion.Swimming ? SWIM_SPEED : groundSpeed * resistance) * factor * Math.min(1, magnitude);
    const currentFactor = mode === WaterLocomotion.Swimming ? 1 : mode === WaterLocomotion.Deep ? 0.16 : 0;
    const current = before.flowSpeed * currentFactor;
    const targetX = (intent ? input.x / magnitude * maximumSpeed : 0) + before.flowDirectionX * current;
    const targetZ = (intent ? input.z / magnitude * maximumSpeed : 0) + before.flowDirectionZ * current;
    const responseRate = mode === WaterLocomotion.Swimming ? (intent ? 4.5 : 5.5)
        : mode === WaterLocomotion.Ground ? (intent ? 13 : 17) : (intent ? 8 : 11);
    const response = 1 - Math.exp(-responseRate * dt);
    state.velocityX += (targetX - state.velocityX) * response;
    state.velocityZ += (targetZ - state.velocityZ) * response;
    const oldX = state.x, oldZ = state.z;
    const [resolvedX, resolvedZ] = options.resolvePosition(oldX + state.velocityX * dt, oldZ + state.velocityZ * dt);
    const correctionX = resolvedX - oldX - state.velocityX * dt;
    const correctionZ = resolvedZ - oldZ - state.velocityZ * dt;
    const correctionLength = Math.hypot(correctionX, correctionZ);
    if (dt > 0 && correctionLength > 1e-6 && options.onContact) {
        const normalX = correctionX / correctionLength, normalZ = correctionZ / correctionLength;
        options.onContact({ attemptedVelocityX: state.velocityX, attemptedVelocityZ: state.velocityZ,
            normalX, normalZ, normalImpactSpeed: Math.max(0, -state.velocityX * normalX - state.velocityZ * normalZ) });
    }
    [state.x, state.z] = options.clampPosition?.(resolvedX, resolvedZ) ?? [resolvedX, resolvedZ];
    if (dt > 0 && Math.abs(state.x - oldX) < 0.00001)
        state.velocityX *= 0.15;
    if (dt > 0 && Math.abs(state.z - oldZ) < 0.00001)
        state.velocityZ *= 0.15;
    const water = sample(state.x, state.z);
    state.locomotion = options.freeFly ? WaterLocomotion.Ground : classifyWaterLocomotion(water.depth, mode);
    if (options.freeFly) {
        state.velocityY += (((input.vertical ?? 0) * 4.2 * factor) - state.velocityY) * (1 - Math.exp(-13 * dt));
        state.y = Math.max(DEVELOPER_MINIMUM_Y, Math.min(DEVELOPER_MAXIMUM_Y, state.y + state.velocityY * dt));
    }
    else {
        const targetY = state.locomotion === WaterLocomotion.Swimming ? water.surfaceY - SWIM_SURFACE_ANCHOR_HEIGHT : water.terrainY;
        const oldY = state.y;
        const transitioning = state.locomotion === WaterLocomotion.Swimming || previous === WaterLocomotion.Swimming
            || (state.locomotion !== WaterLocomotion.Ground && state.y > water.terrainY + 0.01);
        state.y = transitioning ? Math.max(water.terrainY, state.y + (targetY - state.y) * (1 - Math.exp(-WATER_VERTICAL_RESPONSE * dt))) : targetY;
        state.velocityY = dt > 0 ? (state.y - oldY) / dt : 0;
    }
    if (options.updateWetness !== false)
        state.wetness = !options.freeFly && water.depth >= WATER_THRESHOLDS.meaningfulImmersion
            ? 1 : Math.max(0, Math.min(1, state.wetness) - dt / WET_DURATION_SECONDS);
    if (input.aimYaw !== undefined) {
        state.aimYaw = normalizePlayerYaw(input.aimYaw);
        state.aimPitch = clampPlayerPitch(input.aimPitch ?? 0);
        state.yaw = advancePlayerBodyYaw(state.yaw, state.aimYaw, intent, dt);
        return water;
    }
    const actualX = state.x - oldX, actualZ = state.z - oldZ;
    if (dt > 0 && Math.hypot(actualX, actualZ) / dt > 0.08) {
        const desiredYaw = Math.atan2(actualX, actualZ);
        const difference = Math.atan2(Math.sin(desiredYaw - state.yaw), Math.cos(desiredYaw - state.yaw));
        state.yaw += difference * (1 - Math.exp(-12 * dt));
    }
    return water;
}
function smooth(amount) { const t = Math.max(0, Math.min(1, amount)); return t * t * (3 - 2 * t); }
